// The side panel: a tabbed slot beside the main region (note §11.5). Each tab is a registered
// `panel` extension, mounted with the PanelProps contract: the focus, the company, and the
// panel's own feature state. Panels whose add-on is off never appear. The look is
// `@companyos/ui`'s SidePanel (underline tabs; CHAT-PANEL: 360 px to a third of the window,
// dragged from its left edge, kept per user as a share of the window: panel-width.ts).
import { SidePanel as SidePanelFrame, TopbarIconButton } from "@companyos/ui";
import { useRouterState } from "@tanstack/react-router";
import { PanelRightIcon } from "lucide-react";
import { visiblePanels } from "../extensions/panels";
import type { PanelEntry } from "../extensions/registry";
import { featureState, useCompany, useSession } from "../features/use-feature";
import { useFocus } from "../focus/focus";
import { usePanelWidth } from "./panel-width";
import { useShellState } from "./shell-state";

/** The mode segment of `/c/{slug}/{mode}/…`, or null outside a mode. */
export function useModeKey(): string | null {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return pathname.split("/")[3] || null;
}

export function usePanels(): PanelEntry[] {
  const { me } = useSession();
  const company = useCompany();
  return visiblePanels(me, company, useModeKey());
}

export function SidePanelToggle() {
  const panels = usePanels();
  const { panelOpen, setPanelOpen } = useShellState();
  if (panels.length === 0) return null;
  return (
    <TopbarIconButton
      aria-label="Side panel"
      aria-expanded={panelOpen}
      aria-controls={panelOpen ? "side-panel" : undefined}
      className="aria-expanded:bg-primary-soft aria-expanded:text-primary-text"
      onClick={() => setPanelOpen(!panelOpen)}
    >
      <PanelRightIcon aria-hidden="true" />
    </TopbarIconButton>
  );
}

export function SidePanel() {
  const panels = usePanels();
  const { me } = useSession();
  const company = useCompany();
  const { focus, setFocus } = useFocus();
  const modeKey = useModeKey();
  const { panelOpen, setPanelOpen, panelTab, setPanelTab } = useShellState();
  // CHAT-PANEL: resizable from its left edge; the width is the person's, kept per user.
  const size = usePanelWidth(me.user.id);
  if (!panelOpen || panels.length === 0 || !modeKey) return null;
  const active = panels.find((p) => p.key === panelTab) ?? panels[0];
  if (!active) return null;

  return (
    <SidePanelFrame
      id="side-panel"
      value={active.key}
      onValueChange={setPanelTab}
      // The panel's one close button (wave 7: the thread had its own X under this one). Closing
      // on the Thread tab also lets go of the thread, as its own X did.
      onClose={() => {
        if (active.key === "thread" && focus.thread) setFocus({ thread: null });
        setPanelOpen(false);
      }}
      resize={{
        width: size.width,
        min: size.min,
        max: size.max,
        onResize: size.resize,
        onReset: size.reset,
      }}
      tabs={panels.map((p) => {
        const Panel = p.component;
        return {
          value: p.key,
          label: p.labelIn?.[modeKey] ?? p.label,
          flush: p.flush ?? false,
          content: (
            <Panel
              focus={focus}
              company={company}
              feature={featureState(me, company.id, p.feature)}
              modeKey={modeKey}
            />
          ),
        };
      })}
    />
  );
}
